import React, { useCallback, useMemo } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import { useUnit } from 'effector-react';
import moment from 'moment';

import { TableGridLocal } from '../../../../components/TableGridLocal';
import { LimitsApi, LimitsStore } from '../../../../effector/limits';
import { ClientAccountInfo } from '../../../../types/clientFullInfo';
import { limitColumns } from '../constants';

type LimitSectionProps = {
  agreementId: string;
  serviceName: string;
  info: ClientAccountInfo;
  limitsApi: LimitsApi;
  limits$: LimitsStore;
};

export const LimitSection: React.FC<LimitSectionProps> = ({ agreementId, serviceName, info, limitsApi, limits$ }) => {
  const limitsStore = useUnit(limits$);

  const getLimits = useCallback(() => {
    const clientCodes = info.futuresCode ? [info.clientCode, info.futuresCode] : [info.clientCode];
    limitsApi.init({ id: agreementId, codes: clientCodes });
    limitsApi.query(agreementId);
  }, [agreementId, info.clientCode, info.futuresCode, limitsApi]);

  const isLoading = limitsStore.id === agreementId && !limitsStore.complete && !limitsStore.error;
  const isComplete = limitsStore.complete && limitsStore.id === agreementId;
  const positions = useMemo(
    () =>
      isComplete
        ? limitsStore.positions.filter(
            (position) =>
              position.clientCode === info.clientCode ||
              Boolean(info.futuresCode && position.clientCode === info.futuresCode),
          )
        : [],
    [info.clientCode, info.futuresCode, isComplete, limitsStore.positions],
  );

  return (
    <Card variant="outlined">
      <CardHeader title={serviceName} titleTypographyProps={{ variant: 'subtitle1' }} />
      <Divider />
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Button
          variant="contained"
          onClick={getLimits}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {isLoading ? `Получение данных из ${serviceName}…` : `Запросить из ${serviceName}`}
        </Button>

        {limitsStore.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Ошибка получения лимитов: {limitsStore.error}
          </Alert>
        )}

        {isComplete && limitsStore.responseTime && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Лимиты получены в {moment(limitsStore.responseTime).format('DD.MM.YYYY HH:mm:ss')}
          </Typography>
        )}

        {isComplete && positions.length > 0 && (
          <Box sx={{ mt: 2, maxWidth: '100%', overflow: 'hidden' }}>
            <TableGridLocal
              name={`statistic-client-info-${serviceName.toLowerCase()}`}
              columns={limitColumns}
              data={positions}
              pageSize={25}
              totalRowCount={positions.length}
              enablePagination={false}
            />
          </Box>
        )}

        {isComplete && positions.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Позиции не найдены
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
